import {
  actionColumn,
  column,
  Css,
  emptyCell,
  GridColumn,
  GridDataRow,
  Icon,
  IconButton,
  Palette,
  selectColumn,
  simpleHeader,
  Tooltip,
} from "@homebound/beam";
import { dateCell } from "src/components";
import { ItemTemplateStatus, ReadyPlanStatus } from "src/generated/graphqlTypes";
import { useArchiveItemTemplateVersionsMutation } from "src/routes/developments/templates/components/ArchiveItemTemplatesModal.generated";
import { CopyLineItemsModalButton } from "src/routes/developments/templates/components/CopyLineItemsModalButton";
import { useDeleteItemTemplatesMutation } from "src/routes/developments/templates/DevelopmentScopeTemplatePage.generated";
import { DevelopmentScopeTemplateFragment } from "src/routes/developments/templates/DevelopmentScopeTemplates.generated";
import { createDevelopmentScopeTemplateUrl } from "src/RouteUrls";

type HeaderRow = { kind: "header" };

type ItemTemplateRow = { kind: "itemTemplate"; data: DevelopmentScopeTemplateFragment; id: string };
type Row = HeaderRow | ItemTemplateRow;
export type DevelopmentScopeTemplatesTableRow = Row;

export function createScopeTemplateTableRows(itemTemplates: DevelopmentScopeTemplateFragment[]): GridDataRow<Row>[] {
  return [simpleHeader, ...itemTemplates.map((it) => ({ id: it.id, kind: "itemTemplate" as const, data: it }))];
}

export function createScopeTemplateColumns(
  developmentId: string,
  onSave: () => void,
  itemTemplates: DevelopmentScopeTemplateFragment[],
): GridColumn<Row>[] {
  // Only show the archive column when a template's ReadyPlan has been archived out from under it
  const showArchiveCol = itemTemplates.some((it) => {
    const { isArchived, readyPlanIsArchived } = getReadyPlanArchiveStatus(it);
    return !isArchived && readyPlanIsArchived;
  });

  const nameColumn = column<Row>({
    id: "planName",
    header: () => ({ content: "Plan" }),
    itemTemplate: (row) => getNameCellContent(row, developmentId),
    w: 2,
    mw: "240px",
  });

  const versionColumn = column<Row>({
    id: "versionName",
    header: "Version",
    itemTemplate: ({ displayVersion, isVersioned }) => (isVersioned ? `${displayVersion}` : "--"),
    mw: "140px",
  });

  const archiveColumn = actionColumn<Row>({
    header: () => ({ ...emptyCell, css: Css.pl0.$ }),
    itemTemplate: (row) => getArchiveCellContent(row),
    clientSideSort: false,
    w: "16px",
  });

  const statusColumn = column<Row>({
    header: "Status",
    itemTemplate: ({ statusDetail }) => statusDetail.name,
    mw: "100px",
  });

  const createdAtColumn = column<Row>({
    header: "Created",
    itemTemplate: ({ createdAt }) => dateCell(createdAt),
    mw: "110px",
  });

  const updatedAtColumn = column<Row>({
    header: "Last updated",
    itemTemplate: ({ updatedAt }) => dateCell(updatedAt),
    mw: "110px",
  });

  const actionsColumn = actionColumn<Row>({
    header: emptyCell,
    itemTemplate: (row) => getActionCellContent(row, developmentId, onSave),
    w: "60px",
  });

  return [
    selectColumn<Row>(),
    ...(showArchiveCol ? [archiveColumn] : []),
    nameColumn,
    versionColumn,
    statusColumn,
    createdAtColumn,
    updatedAtColumn,
    actionsColumn,
  ];
}

function ArchiveOrDeleteTemplateButton({
  itemTemplate,
  onSave,
}: {
  itemTemplate: Pick<DevelopmentScopeTemplateFragment, "id" | "canDelete">;
  onSave: () => void;
}) {
  const [archiveItemTemplate, { loading: archiving }] = useArchiveItemTemplateVersionsMutation();
  const [deleteItemTemplate, { loading: deleting }] = useDeleteItemTemplatesMutation();

  const deleteTemplate = itemTemplate.canDelete.allowed;

  return (
    <IconButton
      icon={deleteTemplate ? "trash" : "archive"}
      tooltip={deleteTemplate ? "Delete template" : "Archive template"}
      // IconButton doesn't track async onClick like Button, so block repeat clicks while the mutation runs
      disabled={archiving || deleting}
      onClick={async () => {
        if (deleteTemplate) {
          await deleteItemTemplate({ variables: { id: [itemTemplate.id] } });
        } else {
          await archiveItemTemplate({ variables: { templateId: [itemTemplate.id], archiveAllVersions: true } });
        }
        // We requery instead of evict due to the way template versioning works with the `latest` template is hard to predict from the FE
        onSave();
      }}
    />
  );
}

// Renders our action cell content for parent template columns
// Currently there are two possible actions for templates:
// 1. Show a 'Archive`/`Delete` btn depending on the state of the ready plan
// 2. Then 'copy line items' btn as the default state
function getActionCellContent(row: DevelopmentScopeTemplateFragment, developmentId: string, onSave: () => void) {
  const { isArchived, readyPlanIsArchived } = getReadyPlanArchiveStatus(row);
  return {
    content: () => {
      if (!isArchived && readyPlanIsArchived)
        return <ArchiveOrDeleteTemplateButton itemTemplate={row} onSave={onSave} />;

      return row.readyPlan?.id ? (
        <CopyLineItemsModalButton
          templateId={row.id}
          displayName={row.displayName}
          onSave={onSave}
          developmentId={developmentId}
          hasCloneToTargets={row.hasCloneToTargets}
        />
      ) : (
        <></>
      );
    },
  };
}

// Renders our archive cell content for parent template columns.
// If a ReadyPlan has been archived but we have not archived the template,
// we show an Icon letting the user know to archive their template
function getArchiveCellContent(row: DevelopmentScopeTemplateFragment) {
  const { isArchived, readyPlanIsArchived } = getReadyPlanArchiveStatus(row);

  return {
    content: () =>
      !isArchived && readyPlanIsArchived ? (
        <div css={Css.p0.$}>
          <Tooltip title="This ReadyPlan has been archived">
            <Icon icon="error" color={Palette.Red600} inc={2} />
          </Tooltip>
        </div>
      ) : (
        <></>
      ),
    css: Css.pl0.$,
  };
}

// Renders our name cell content for parent/child template columns
function getNameCellContent(row: DevelopmentScopeTemplateFragment, developmentId: string) {
  return { content: row.displayName, onClick: createDevelopmentScopeTemplateUrl(developmentId, row.id) };
}

// Returns whether an ItemTemplate isArchived, whether or not its ReadyPlan is archivedts
function getReadyPlanArchiveStatus(it: DevelopmentScopeTemplateFragment) {
  const isArchived = it.statusDetail.code === ItemTemplateStatus.Archived;

  const readyPlanIsArchived = it.readyPlan && it.readyPlan?.status.code === ReadyPlanStatus.Archived;

  return { isArchived, readyPlanIsArchived };
}

export enum Column {
  Select,
  TemplateName,
  Version,
  Status,
  Created,
  LastUpdated,
  Actions,
}
